import React, { useState } from 'react';
import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, { Crop } from 'react-image-crop';
import { ImageAttributes } from 'types/general.types';
import { Box, useStyleConfig } from '@chakra-ui/react';
import { CROP_INITIAL_STATE, MIN_ALLOWED_DIMENSION, Unit } from '../constants';

export interface ImageCropProps {
  src: string;
  onCropChange: (crop: Crop) => void;
  setImageAttributes: (value: ImageAttributes) => void;
}

export const ImageCrop: React.FC<ImageCropProps> = ({
  src,
  onCropChange,
  setImageAttributes,
}) => {
  const [crop, setCrop] = useState<Crop>(CROP_INITIAL_STATE);

  const onUpdate = (crop: Crop) => {
    const { width } = crop;
    if (width >= MIN_ALLOWED_DIMENSION) {
      setCrop(crop);
      onCropChange(crop);
    } else {
      setCrop(CROP_INITIAL_STATE);
      onCropChange(CROP_INITIAL_STATE);
    }
  };

  const handleOnImageLoaded = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const {
      naturalWidth,
      naturalHeight,
      clientHeight,
      clientWidth,
    } = e.currentTarget;

    const min = Math.min(clientWidth, clientHeight);

    let dimension = Math.floor(min / 1.5);

    if (dimension < MIN_ALLOWED_DIMENSION) {
      dimension = MIN_ALLOWED_DIMENSION;
    }

    if (clientWidth < dimension || clientHeight < dimension) {
      dimension = min;
    }

    setImageAttributes({
      naturalHeight,
      naturalWidth,
      height: clientHeight,
      width: clientWidth,
    });

    const crop = {
      width: dimension,
      height: dimension,
      y: Math.floor((clientHeight - dimension) / 2),
      x: Math.floor((clientWidth - dimension) / 2),
      unit: Unit.px,
    };
    onUpdate(crop);

    return false;
  };

  const styles = useStyleConfig('ImageCrop', {});

  return (
    <Box sx={styles}>
      <ReactCrop
        crop={crop}
        onComplete={onUpdate}
        onChange={onUpdate}
        minWidth={MIN_ALLOWED_DIMENSION}
        minHeight={MIN_ALLOWED_DIMENSION}
        aspect={1}
        keepSelection
      >
        <img src={src} alt="Profile" onLoad={handleOnImageLoaded} />
      </ReactCrop>
    </Box>
  );
};
